// v2.71.73 — ONE central Google Maps URL utility for the whole platform.
// (Was: v2.71.14 — local-only parser; v2.71.23 — added re-export shim.)
//
// What this module exports:
//   • `extractCoordinates(url)`         → pure regex lat/lng extractor
//   • `isShortMapsUrl(url)`             → recognises maps.app.goo.gl/goo.gl
//   • `parseGoogleMapsUrl(url)`         → local-only validator (no network)
//   • `validateGoogleMapsUrl(url)`      → backwards-compat alias
//   • `resolveMapsUrl(url, api)`        → ASYNC — calls /api/maps/resolve to
//                                          follow short-URL redirects + extract
//                                          coordinates server-side. ONE source
//                                          of truth used by every form.
//
// Supported URL shapes (handled locally for full URLs, server-resolved
// for short URLs):
//   • https://maps.app.goo.gl/<id>            ← mobile-app share (short)
//   • https://goo.gl/maps/<id>                ← legacy short
//   • https://www.google.com/maps/<…>         ← full place/share/dir URL
//   • https://maps.google.com/?q=LAT,LNG      ← coords-in-querystring
//   • https://google.<tld>/maps/<…>           ← regional (.co.in, .de…)
//   • /@LAT,LNG,ZOOM                          ← /maps/@28.6,77.2,15z
//   • !3dLAT!4dLNG                            ← encoded in /maps/place/…
//   • /search/LAT,LNG | /dir/…/LAT,LNG/       ← search + directions

const PATTERNS = [
  /[?&]q=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i,
  /[?&]ll=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i,
  /[?&]center=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i,
  /[?&]query=(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i,
  /@(-?\d+\.\d+),(-?\d+\.\d+)(?:,[\d.]+z)?/i,
  /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/i,
  /\/search\/(-?\d+\.\d+),(-?\d+\.\d+)/i,
  /\/dir\/.*\/(-?\d+\.\d+),(-?\d+\.\d+)\/?/i,
];

const SHORT_HOSTS = ["maps.app.goo.gl", "goo.gl"];

const ALLOWED_HOSTS = [
  "maps.google.com",
  "www.google.com",
  "google.com",
  "maps.app.goo.gl",
  "goo.gl",
];

function _coordsValid(lat, lng) {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180 &&
    !(lat === 0 && lng === 0)
  );
}

function _hostOf(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return "";
  }
}

function _isGoogleHost(host) {
  if (ALLOWED_HOSTS.includes(host)) return true;
  return /^(www\.)?google\.[a-z.]+$/.test(host);
}

/**
 * Extract { lat, lng } from any supported full Google Maps URL shape.
 * Returns null for short URLs and any URL without recognised coord patterns.
 * Local-only — no network. For short URLs, use `resolveMapsUrl()`.
 */
export function extractCoordinates(url) {
  const raw = (url || "").trim();
  if (!raw) return null;
  const candidates = [raw];
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded !== raw) candidates.push(decoded);
  } catch (_) {
    /* ignore */
  }
  for (const candidate of candidates) {
    for (const pattern of PATTERNS) {
      const m = candidate.match(pattern);
      if (m) {
        const lat = Number(m[1]);
        const lng = Number(m[2]);
        if (_coordsValid(lat, lng)) return { lat, lng };
      }
    }
  }
  return null;
}

export function isShortMapsUrl(url) {
  return SHORT_HOSTS.includes(_hostOf(url));
}

/**
 * Local-only validator. Returns:
 *   { valid: true,  coords: { lat, lng } }     → ready to save (full URL parsed)
 *   { valid: true,  coords: null, short: true} → short URL — caller should call
 *                                                 resolveMapsUrl() to expand it
 *   { valid: false, reason: "..." }            → malformed / non-Google URL
 *
 * Empty input → { valid: true, coords: null } (URL field is optional).
 */
export function parseGoogleMapsUrl(url) {
  const raw = (url || "").trim();
  if (!raw) return { valid: true, coords: null };

  if (!/^https?:\/\//i.test(raw)) {
    return { valid: false, reason: "URL must start with http:// or https://" };
  }
  const host = _hostOf(raw);
  if (!host) return { valid: false, reason: "Invalid URL format" };
  if (!_isGoogleHost(host)) {
    return { valid: false, reason: "Please paste a Google Maps URL" };
  }
  if (SHORT_HOSTS.includes(host)) {
    // v2.71.73 — Short URLs are NOW valid. Caller must call resolveMapsUrl()
    // to fetch the canonical URL + coords from /api/maps/resolve.
    return { valid: true, coords: null, short: true };
  }
  // google.com path must include /maps
  if (host === "google.com" || host === "www.google.com" || /^(www\.)?google\.[a-z.]+$/.test(host)) {
    try {
      const u = new URL(raw);
      if (!/\/maps(\/|$|\?)/i.test(u.pathname)) {
        return { valid: false, reason: "Link must point to /maps" };
      }
    } catch {
      /* unreachable */
    }
  }
  const coords = extractCoordinates(raw);
  if (!coords) {
    // Full Google /maps URL but no coords in it (e.g. /maps/place/Foo without @lat,lng).
    // Treat as "valid but indeterminate" — server-side resolve may still help
    // (e.g. place share URLs without coords get redirected to a canonical URL).
    return { valid: true, coords: null, indeterminate: true };
  }
  return { valid: true, coords };
}

/**
 * Backwards-compat alias.
 */
export function validateGoogleMapsUrl(url) {
  return parseGoogleMapsUrl(url);
}

/**
 * ASYNC — resolve short URL + extract coords via /api/maps/resolve.
 * Use this for short URLs (maps.app.goo.gl / goo.gl) and as a fallback
 * for full URLs that don't parse locally.
 *
 * @param {string} url  - The pasted URL
 * @param {object} api  - axios instance (caller-supplied to keep this util
 *                        framework-agnostic — pass the shared `api` import).
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal] - For request cancellation
 *
 * Returns:
 *   { ok: true,  coords: {lat,lng}, normalizedUrl, resolved: bool }
 *   { ok: false, reason: string, normalizedUrl?: string }
 */
export async function resolveMapsUrl(url, api, opts = {}) {
  const raw = (url || "").trim();
  if (!raw) return { ok: false, reason: "Empty URL" };

  // Fast-path: local parse first (no network for full URLs with coords)
  const local = parseGoogleMapsUrl(raw);
  if (!local.valid) {
    return { ok: false, reason: local.reason };
  }
  if (local.coords) {
    return {
      ok: true,
      coords: local.coords,
      normalizedUrl: raw,
      resolved: false,
    };
  }

  // Network: short URL OR indeterminate full URL → call backend
  if (!api) {
    return { ok: false, reason: "API client not provided" };
  }
  try {
    const res = await api.post(
      "/maps/resolve",
      { url: raw },
      opts.signal ? { signal: opts.signal } : undefined
    );
    const data = res?.data || {};
    if (data.ok && Number.isFinite(data.latitude) && Number.isFinite(data.longitude)) {
      return {
        ok: true,
        coords: { lat: data.latitude, lng: data.longitude },
        normalizedUrl: data.normalized_url || raw,
        resolved: Boolean(data.resolved),
      };
    }
    return {
      ok: false,
      reason: data.reason || "Could not resolve this URL.",
      normalizedUrl: data.normalized_url,
    };
  } catch (e) {
    if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") {
      return { ok: false, reason: "cancelled", cancelled: true };
    }
    return {
      ok: false,
      reason: "Could not reach the link resolver. Try Pick Location instead.",
    };
  }
}
